import { ContractStatus } from "@boark/shared/types/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  ContractForm,
  ContractFormValues,
} from "../../components/contract-form";
import { Modal } from "../../components/modal";
import {
  contractFormValuesToCreate,
  createContract,
} from "../../services/contract";
import { updateProcurement } from "../../services/procurement";
import { navigateBack } from "../../utils/navigate-back";
import { recordIdToId } from "../../utils/record-id-to-id";

export function ContractCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const procurementId = searchParams.get("procurementId");
  const initialCounterPartyId = searchParams.get("counterPartyId") ?? "";
  const initialTitle = searchParams.get("title") ?? "";
  const returnRoute = procurementId
    ? `/admin/procurement/${procurementId}`
    : "/admin/contract/list";

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<ContractFormValues>({
    title: initialTitle,
    description: "",
    tags: [],
    status: ContractStatus.ACTIVE,
    cost: "",
    invoiceType: "",
    fromDate: "",
    toDate: "",
    counterPartyId: initialCounterPartyId,
  });

  const closeModal = () => {
    navigateBack(navigate, returnRoute);
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <ContractForm
        hasError={hasError}
        loading={loading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              const createdContract = await createContract(
                contractFormValuesToCreate(formValues),
              );
              if (procurementId) {
                await updateProcurement(procurementId, {
                  contractId: recordIdToId(createdContract.id),
                });
                await queryClient.invalidateQueries({
                  queryKey: ["procurement", procurementId],
                });
              }
              await queryClient.invalidateQueries({ queryKey: ["contracts"] });
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
